import {
	contentFormats,
	kindName,
	type PlatformContract,
	type PublishPayload,
	suggestFormat,
	withFormat,
} from "@ondit/publishing";
import { Button } from "@ondit/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ChevronRight, ExternalLink } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { useAccess } from "~/core/access/index";
import { api, type Block, type PageDetail } from "~/core/api/index";
import { pick, useI18n } from "~/core/i18n";
import { useBrandContracts } from "~/core/publishing";
import { keys, read } from "~/core/query";
import { Async } from "~/core/ui/states";
import { PublishingContentEditor } from "~/modules/analytics/features/platform-publishing/content-editor";
import { articleMarkdown } from "~/modules/analytics/lib/distribution";
import { fitTo } from "./fit";
import { issueLabel } from "./issues";
import { type PlatformStatus, platformStatus } from "./platform-status";

const LAYOUTS: Record<string, [string, string]> = {
	article: ["Articles", "长文"],
	newsletter: ["Newsletters", "邮件订阅"],
	gallery: ["Image posts", "图文"],
	pin: ["Image posts", "图文"],
	video: ["Video", "视频"],
	social: ["Social", "社交"],
	discussion: ["Communities", "社区"],
	business: ["Local", "本地商家"],
	chat: ["Chat", "群聊频道"],
};

const variantsKey = (brandId: string, pageId: string) => [...keys.entity(brandId), "page", pageId, "variants"];

/**
 * The article on each platform it is going to.
 *
 * A list first — one row a platform, its state and the one thing it is
 * waiting on — and one platform at a time after that, because a version is
 * read, edited and approved alone and the panel is too narrow for two.
 */
export function PlatformVersions({
	brandId,
	pageId,
	data,
	draft,
	initialPlatform = "",
	initialFormat = "",
	taskId,
	onDetailChange,
}: {
	brandId: string;
	pageId: string;
	data: PageDetail;
	draft: { title: string; description: string; blocks: Block[] };
	initialPlatform?: string;
	initialFormat?: string;
	taskId?: string;
	onDetailChange?: (open: boolean) => void;
}) {
	const { locale } = useI18n();
	const { editor } = useAccess();
	const tr = (en: string, zh: string) => pick(locale, [en, zh]);
	const cache = useQueryClient();
	const contracts = useBrandContracts(brandId);
	const [open, setOpen] = useState<string | null>(initialPlatform || null);
	// The kind of post on the open platform; "" until somebody switches.
	const [format, setFormat] = useState(initialFormat);
	const versions = useQuery({
		queryKey: variantsKey(brandId, pageId),
		queryFn: () => api.pageVariants(pageId),
		...read,
	});
	const distribution = useQuery({
		queryKey: [...keys.entity(brandId), "distribution"],
		queryFn: () => api.distribution(brandId),
		...read,
	});
	const source = useQuery({
		queryKey: [...keys.entity(brandId), "distribution-source", pageId],
		queryFn: () => api.distributionSource(brandId, pageId),
		...read,
	});
	const tasks = useQuery({
		queryKey: [...keys.entity(brandId), "platform-tasks"],
		queryFn: () => api.platformTasks(brandId),
		...read,
	});
	const reload = () => void cache.invalidateQueries({ queryKey: variantsKey(brandId, pageId) });
	// The platforms picked, and any that already has a version.
	const shown = [
		...new Set([
			...(initialPlatform ? [initialPlatform] : []),
			...(versions.data?.variants.map((v) => v.platform) ?? []),
		]),
	]
		.map((id) => contracts.byId(id))
		.filter((c): c is PlatformContract => !!c);
	const work = { title: draft.title, summary: draft.description, pieces: draft.blocks };
	/* Which kind of post this article would go out as: the one a version already
	 * made decided, then what the article most naturally becomes. Both the list
	 * and the detail below ask, because checking an article against a platform's
	 * default rather than against its kind is how a piece with no video came to
	 * read as "needs a video" on a platform that also publishes articles. */
	const kindFor = (contract: PlatformContract, chosen?: string | null) =>
		withFormat(
			contract,
			chosen ||
				versions.data?.variants.find((v) => v.platform === contract.id)?.format ||
				suggestFormat(contract, {
					length: source.data ? articleMarkdown(source.data).length : 0,
					video: draft.blocks.some((b) => b.type === "video"),
					images: draft.blocks.filter((b) => b.type === "image").length,
				}),
		) ?? contract;
	const statusOf = (contract: PlatformContract) =>
		platformStatus(
			fitTo(work, kindFor(contract)),
			versions.data?.variants.find((v) => v.platform === contract.id),
			(issue) => {
				// A field named in an issue reads by the platform's own label for it.
				const field = contract.preview.fields.find((f) => f.key === issue.split(":")[1]);
				const name = (locale: "en" | "zh") =>
					field ? issueLabel(issue, locale).replace(field.key, pick(locale, field.label)) : issueLabel(issue, locale);
				return [name("en"), name("zh")];
			},
			tasks.data?.tasks.find(
				(t) => t.payload.source.page_id === pageId && t.payload.platform === contract.id && t.state !== "cancelled",
			),
		);
	const current = shown.find((c) => c.id === open);
	if (current) {
		// A version already made decides the kind first; then what the article
		// most naturally becomes — its video, or the long form past a short post.
		const kind = kindFor(current, format);
		const variant = versions.data?.variants.find((v) => v.platform === current.id && v.format === (kind.format ?? ""));
		const row = distribution.data?.platforms.find((p) => p.id === current.id);
		const status = statusOf(current);
		// Before the article is live a post has nowhere to link, so an edit is
		// kept as the version and sending waits for the publish.
		const live = data.status === "published";
		const saveDraft = async (payload: PublishPayload) => {
			const kindId = kind.format ? `${current.id}:${kind.format}` : current.id;
			const target = variant ?? (await api.adaptPage(brandId, pageId, [kindId])).output?.variants[0];
			if (!target) return;
			await api.saveVariant(brandId, target.id, {
				expected_revision: target.revision,
				fields: payload.fields,
				file_ids: payload.media.flatMap((m) => (m.url.startsWith("ondit-file:") ? [m.url.slice(11)] : [])),
				target_url: payload.target_url,
			});
			toast.success(tr("Version saved", "渠道版本已保存"));
			reload();
		};
		return (
			<div className="platform-detail">
				<header className="platform-detail-head">
					<Button
						variant="ghost"
						size="icon"
						aria-label={tr("All platforms", "全部渠道")}
						onClick={() => {
							setOpen(null);
							onDetailChange?.(false);
						}}
					>
						<ArrowLeft />
					</Button>
					<h3>{kindName(current, kind.format, locale)}</h3>
					{status.link ? (
						<a className="platform-list-link" href={status.link} target="_blank" rel="noreferrer">
							<ExternalLink aria-hidden />
							{tr("Open the post", "打开已发布内容")}
						</a>
					) : null}
					<StatusPill status={status} />
					<Button variant="ghost" disabled={!editor} asChild>
						<Link to={`/w/${encodeURIComponent(brandId)}/publish/distribution?${new URLSearchParams({tab:"prepare",page:pageId})}`}>
							{tr("Open distribution plan", "打开分发计划")}
						</Link>
					</Button>
				</header>
				{/* The same editor the distribution screen draws. */}
				<Async state={source}>
					{(article) =>
						row ? (
							<PublishingContentEditor
								key={`${article.id}:${article.revision}:${current.id}:${kind.format ?? ""}:${variant?.revision ?? 0}`}
								source={article}
								platform={row}
								format={kind.format}
								onFormatChange={setFormat}
								entityId={brandId}
								taskId={taskId ?? variant?.task_id ?? undefined}
								seed={
									variant && {
										fields: variant.document.fields,
										media: variant.document.media.map((m) => ({ url: `ondit-file:${m.file_id}`, alt: m.alt })),
										target_url: variant.document.target_url,
									}
								}
								saveDraft={live ? undefined : saveDraft}
							/>
						) : null
					}
				</Async>
			</div>
		);
	}

	return (
		<div className="stack">
			<section className="article-platform-versions">
				<h3>{tr("Existing platform versions", "已有渠道版本")}</h3>

				{shown.length === 0 ? (
					<p className="hint">{tr("No saved channel versions yet.", "尚无已保存的渠道版本。")}</p>
				) : (
					<ul className="platform-list">
						{shown.map((contract) => {
							const status = statusOf(contract);
							// The kind this would go out as, not the platform's default: a row
							// reading "video" under an article is the wrong half of the answer.
							const kind = kindFor(contract);
							const note = contentFormats(contract).find((f) => f.id === kind.format)?.label;
							return (
								<li key={contract.id}>
									<button
										type="button"
										onClick={() => {
											setOpen(contract.id);
											onDetailChange?.(true);
											setFormat("");
										}}
									>
										<span className="platform-list-name">{contract.name}</span>
										<span className="platform-list-note hint">
											{status.problems[0]
												? pick(locale, status.problems[0])
												: pick(locale, note ?? LAYOUTS[kind.preview.layout] ?? ["", ""])}
										</span>
										<StatusPill status={status} />
										<ChevronRight aria-hidden />
									</button>
									{/* The post itself. A row that says "Published" and offers no way
								    to open it withholds the one thing somebody wants next. */}
									{status.link ? (
										<a className="platform-list-link" href={status.link} target="_blank" rel="noreferrer">
											<ExternalLink aria-hidden />
											{tr("Open the post", "打开已发布内容")}
										</a>
									) : null}
								</li>
							);
						})}
					</ul>
				)}
			</section>
		</div>
	);
}

function StatusPill({ status }: { status: PlatformStatus }) {
	const { locale } = useI18n();
	return (
		<span className={status.tone === "neutral" ? "pill" : `pill pill-${status.tone}`}>
			{pick(locale, status.label)}
		</span>
	);
}
